import { ComponentService } from "app/_core/services/component.service";
import { ShoppingCartState } from "./shopping-cart.state";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { Router, NavigationEnd } from "@angular/router";
import { filter } from "rxjs/operators";
import { ProductType } from "app/_core/services/models/product.model";
import { DodoCartProduct, ProductInfo } from "./dodo-cart-product.class";
import { DodoProductType, CartAmount } from "./shopping-cart.models";
import { PlanService } from "app/plan/plan.service";
import { DodoProduct } from "app/plan/plan.models";

export enum ShoppingCartEvent {
	INITIALIZED,
	PRODUCT_UPDATED,
}

@Injectable({
	providedIn: "root",
})
export class ShoppingCartService extends ComponentService<ShoppingCartState> {
	public productsUpdated$: BehaviorSubject<DodoCartProduct[]>;
	public productRemoved$: BehaviorSubject<DodoProductType>;
	public productAdded$: BehaviorSubject<DodoCartProduct>;
	public checkoutButtonUpdated$: BehaviorSubject<boolean>;
	public checkoutButtonClicked$: BehaviorSubject<boolean>;
	public checkoutButtonBusyStatusUpdated$: BehaviorSubject<boolean>;
	public mobileProductDataChanged$: BehaviorSubject<boolean>;

	protected _localStorageKey = "shopping-cart";
	private slideInPanelActive = true;
	private totalCostAmount: BehaviorSubject<CartAmount>;

	constructor(private __router: Router, private __planService: PlanService) {
		super();

		this.productsUpdated$ = new BehaviorSubject([]);
		this.productRemoved$ = new BehaviorSubject(undefined);
		this.productAdded$ = new BehaviorSubject(undefined);
		this.checkoutButtonUpdated$ = new BehaviorSubject(false);
		this.checkoutButtonClicked$ = new BehaviorSubject(false);
		this.checkoutButtonBusyStatusUpdated$ = new BehaviorSubject(false);
		this.mobileProductDataChanged$ = new BehaviorSubject(false);
		this.totalCostAmount = new BehaviorSubject<CartAmount>(null);

		this.__restoreShoppingCart();

		this.productsUpdated$.pipe(filter((v) => !!v)).subscribe(() => {
			this._storeState();
		});

		this.__router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe(({ urlAfterRedirects }: NavigationEnd) => {
				if (urlAfterRedirects.startsWith("/checkout")) {
					this.slideInPanelActive = false;
				} else {
					this.slideInPanelActive = true;
				}
			});

		this.__planService.productSelected$
			.pipe(filter((val) => !!val))
			.subscribe((productSelection) => {
				this.__updateProduct(productSelection);
			});


		if (!this._state.products) {
			this._state.products = [];
		}
	}

	private __restoreShoppingCart() {
		this._restoreState();
		this.productsUpdated$.next(this._state.products);
	}

	get products(): DodoCartProduct[] {
		return this._state.products;
	}

	getValue(): Observable<CartAmount> {
		return this.totalCostAmount.asObservable();
	}

	setValue(newValue): void {
		this.totalCostAmount.next(newValue);
	}

	addProduct(productType: ProductType) {
		const dodoProduct = new DodoCartProduct(productType);
		console.log("dodo product : ", dodoProduct);
		this._state.products.push(dodoProduct);
		this.productsUpdated$.next(this._state.products);
		this.productAdded$.next(dodoProduct);
	}

	removeProduct(productType: ProductType | DodoProductType) {
		const idx = this._state.products
			.map((p) => p.type)
			.indexOf(productType);
		if (idx >= 0) {
			this._state.products.splice(idx, 1);
			this.productRemoved$.next(productType);
		}
		this.productsUpdated$.next(this._state.products);
	}

	__updateProduct(dodoProduct: DodoProduct) {
		const idx = this._state.products
			.map((p) => p.type)
			.indexOf(dodoProduct.type);

		const product = this._state.products[idx];
		if (product) {
			product.product = dodoProduct;
			const productInfo = <ProductInfo>{
				title: dodoProduct.subText,
			};
			if (product.type === "internet-nbn" || product.type === "mobile") {
				productInfo.features = dodoProduct.planInfo;
			} else if (product.type === "electricity" || product.type === "gas") {
				productInfo.info = `10 business days cooling off period. If you need assistance, call 1300 040 447.`;
				productInfo.features = ["No lock in contract"];
			}
			product.productInfo = productInfo;
		}
		this.productsUpdated$.next(this._state.products);
	}

	hasProduct(productType: ProductType) {
		return this._state.products.filter((p) => p.type === productType)[0]
			? true
			: false;
	}

	clearCart() {
		this._state.products = [];
		this.productsUpdated$.next(this._state.products);
	}

	get hasMobile(): boolean {
		return !!this._state.products.filter((p) => p.type === "mobile")[0];
	}


	get totalCost(): CartAmount {
		let dueToday = 0;
		let monthly = 0;
		let promoDiscountValue = 0;
		let promoDiscountDurationText = "";
		let promoDiscountDurationTextDiscount = "";

		this.products.forEach((p) => {
			if (p && p.product) {
				console.log("p : ", p);
				dueToday += p.product.dueTodayCost;
				monthly += p.product.monthlyCost;
				promoDiscountValue += p.product.promoDiscountValue;
				promoDiscountDurationText = p.product.promoDiscountDurationText;
				promoDiscountDurationTextDiscount =
					p.product.promoDiscountDurationTextDiscount;
			}
		});

		const cartAmount = <CartAmount>{
			dueToday: dueToday,
			monthly: monthly,
			promoDiscountValue: promoDiscountValue,
			promoDiscountDurationText: promoDiscountDurationText,
			promoDiscountDurationTextDiscount: promoDiscountDurationTextDiscount,
		};

		this.setValue(cartAmount);

		return cartAmount;
	}
}
